// Copyright (c) 2025 Greener Power Solutions.
// This file is part of Greener Power Solutions' software,
// and may not be altered, copied, redistributed, or sold
// without specific permission.
import { useTheme } from '@mui/material/styles';
import ProjectSidebarHeader from 'components/layout/ProjectSidebarHeader';
import useAuth from 'hooks/useAuth';
import useProject from 'hooks/useProject';
import { useSidebarItems } from 'hooks/useSidebarItems';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useParams } from 'react-router-dom';
import { canSeeAnalyticalData } from 'utils/authorization';
import { getSortedByActivityUnits } from 'utils/units';
import { Icons } from 'greener-ui-library';

function constructProjectMenu({ t, projectSlug }) {
  const menuItems = [
    {
      name: t('projects:navigation.home'),
      path: `/projects/${projectSlug}`,
    },
    {
      name: t('projects:navigation.overview'),
      path: `/projects/${projectSlug}/administration`,
    },
    {
      name: t('projects:navigation.configurationDiagram'),
      path: `/projects/${projectSlug}/setups`,
    },
  ];

  return {
    isVisible: true,
    name: t('projects:navigation.project'),
    icon: () => <Icons.Project color="secondary" />,
    items: menuItems,
  };
}

function constructMonitoringMenu({ t, projectSlug, unitId, theme }) {
  if (!unitId) {
    return null;
  }

  const menuItems = [
    {
      name: t('projects:navigation.unitControl'),
      path: `/projects/${projectSlug}/monitoring/${unitId}/asset`,
    },
    {
      name: t('projects:navigation.basicGraphs'),
      path: `/projects/${projectSlug}/monitoring/${unitId}/basic-graphs`,
    },
    {
      name: t('projects:navigation.advancedGraphs'),
      path: `/projects/${projectSlug}/monitoring/${unitId}/advanced-graphs`,
    },
  ];

  return {
    isVisible: true,
    name: t('projects:navigation.unitMonitoring'),
    icon: () => <Icons.Graphs strokeColor={theme.palette.secondary.main} />,
    items: menuItems,
  };
}

function constructAnalyticsMenu({ t, projectSlug, userRole, theme }) {
  const menuItems = [
    {
      name: t('projects:navigation.reports'),
      path: `/projects/${projectSlug}/analytics/reports`,
    },
    {
      name: t('projects:navigation.analyticsSummary'),
      path: `/projects/${projectSlug}/analytics/summary`,
    },
    {
      name: t('projects:navigation.assetAnalytics'),
      path: `/projects/${projectSlug}/analytics/assets`,
    },
  ];

  return {
    isVisible: canSeeAnalyticalData(userRole),
    name: t('projects:navigation.analytics'),
    icon: () => <Icons.Analytics strokeColor={theme.palette.secondary.main} />,
    items: menuItems,
  };
}

function determinePreselectedUnitExtId(unitMonitoringMatch, units) {
  if (unitMonitoringMatch) {
    return unitMonitoringMatch.params.unitExternalIdentifier;
  }

  if (units && units.length > 0) {
    const sortedUnits = getSortedByActivityUnits(units);
    return sortedUnits[0]?.externalIdentifier;
  }

  return null;
}

export function SidebarItems() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { projectSlug } = useParams();
  const unitMonitoringMatch = useMatch('/projects/:projectSlug/monitoring/:unitExternalIdentifier/*');
  const { dispatchSidebar } = useSidebarItems();
  const { project, projectUnitInstances } = useProject();
  const theme = useTheme();

  const unitExternalIdentifier = determinePreselectedUnitExtId(unitMonitoringMatch, projectUnitInstances);

  useEffect(() => {
    const projectMenu = constructProjectMenu({ t, projectSlug });
    const monitoringMenu = constructMonitoringMenu({ t, projectSlug, unitId: unitExternalIdentifier, theme });
    const analyticsMenu = constructAnalyticsMenu({ t, projectSlug, userRole: user?.role, theme });
    const items = [projectMenu, monitoringMenu, analyticsMenu].filter((i) => i !== null && i.isVisible);

    dispatchSidebar?.({
      type: 'REGULAR',
      payload: {
        sidebarHeader: <ProjectSidebarHeader project={project} />,
        items,
      },
    });
  }, [project, user, projectSlug, dispatchSidebar, t, unitExternalIdentifier]);
}

SidebarItems.displayName = '[Project].sidebar.tsx';
